import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import './AdminLayout.css';
import { useAuth } from '../features/auth/AuthContext';
import { adminStatsAPI } from '../features/auth/services/api';
import api from '../features/auth/services/api';

const QUICK_LINKS = [
  { label: '🏢 Organizations', path: '/admin/orgs', desc: 'Manage orgs, credits & access codes' },
  { label: '👥 Users', path: '/admin/users', desc: 'Browse and filter all registered users' },
  { label: '📁 Projects', path: '/admin/projects', desc: 'Manage internship projects & stages' },
  { label: '🔍 Stage Reviews', path: '/admin/reviews', desc: 'Review pending stage submissions' },
  { label: '📊 Stats', path: '/admin/stats', desc: 'LLM usage & submission analytics' },
  { label: '💰 Pricing', path: '/admin/pricing', desc: 'Configure platform pricing & packages' },
];

const AdminDashboardPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [llm, setLlm] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      api.get('/admin/stats/submissions'),
      adminStatsAPI.getLLMUsage(),
    ]).then(([subRes, llmRes]) => {
      if (subRes.status === 'fulfilled') setStats(subRes.value.data);
      if (llmRes.status === 'fulfilled') setLlm(llmRes.value.data);
    }).finally(() => setLoading(false));
  }, []);

  const statCards = [
    {
      label: 'Total Submissions',
      value: loading ? '…' : (stats?.total_submissions ?? '—'),
      icon: '📝',
      color: '#6c63ff',
    },
    {
      label: 'Avg Score',
      value: loading ? '…' : (stats?.average_score != null ? `${stats.average_score}%` : '—'),
      icon: '🏆',
      color: '#4ade80',
    },
    {
      label: 'Last 24 h',
      value: loading ? '…' : (stats?.recent_24h ?? '—'),
      icon: '⏱',
      color: '#facc15',
    },
    {
      label: 'LLM Requests',
      value: loading ? '…' : (llm?.totals?.requests ?? '—'),
      icon: '🤖',
      color: '#38bdf8',
    },
    {
      label: 'LLM Cost (USD)',
      value: loading ? '…' : (llm?.totals?.cost_usd != null ? `$${llm.totals.cost_usd.toFixed(4)}` : '—'),
      icon: '💰',
      color: '#fb923c',
    },
  ];

  return (
    <AdminLayout title="🛠 Admin Dashboard">
      {/* Welcome banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #0f172a 100%)',
        border: '1px solid #312e81',
        borderRadius: 12,
        padding: '20px 28px',
        marginBottom: 28,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, color: '#e0e7ff' }}>
            Welcome back, {user?.full_name || 'Admin'} 👋
          </h2>
          <p style={{ margin: '4px 0 0', color: '#818cf8', fontSize: 13 }}>{user?.email}</p>
        </div>
        {llm && (
          <div style={{
            background: '#312e81',
            borderRadius: 8,
            padding: '8px 16px',
            fontSize: 13,
            color: '#c7d2fe',
          }}>
            🤖 <strong style={{ color: '#e0e7ff' }}>{llm.current_provider}</strong>
            {' / '}
            <span style={{ color: '#a5b4fc' }}>{llm.current_model}</span>
          </div>
        )}
      </div>

      {/* Stats cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
        gap: 16,
        marginBottom: 32,
      }}>
        {statCards.map(card => (
          <div key={card.label} className="admin-card" style={{
            padding: '20px',
            textAlign: 'center',
            borderTop: `3px solid ${card.color}`,
          }}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{card.icon}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: card.color }}>
              {card.value}
            </div>
            <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>{card.label}</div>
          </div>
        ))}
      </div>

      {/* Quick links */}
      <h3 style={{ color: '#aaa', fontSize: 13, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
        Quick Access
      </h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 14,
      }}>
        {QUICK_LINKS.map(item => (
          <Link
            key={item.path}
            to={item.path}
            style={{ textDecoration: 'none' }}
          >
            <div className="admin-card" style={{
              padding: '18px 20px',
              cursor: 'pointer',
              transition: 'border-color 0.2s, transform 0.15s',
              borderLeft: '3px solid #6c63ff',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderLeftColor = '#8b5cf6'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderLeftColor = '#6c63ff'; e.currentTarget.style.transform = 'none'; }}
            >
              <div style={{ fontWeight: 600, color: '#e2e8f0', fontSize: 15, marginBottom: 4 }}>
                {item.label}
              </div>
              <div style={{ fontSize: 12, color: '#666', lineHeight: 1.4 }}>{item.desc}</div>
            </div>
          </Link>
        ))}
      </div>
    </AdminLayout>
  );
};

export default AdminDashboardPage;
